// @ts-nocheck
import _ from 'lodash';
import helpers from '@/utils/helpers';
import getTeamDisplayName from '@staty/shared/utils/getTeamDisplayName';
import getDefaultGameDuration from '@staty/shared/utils/getDefaultGameDuration';
import {
  isParseableTime,
  parseTimeToMinutes,
  formatMinutesForGameTime,
  toDateKey,
  toGlobalDate,
} from '@staty/shared/utils/gameDateTime';

import type { Issue, RawGameRow, ResolvedGameRow } from './types';

export interface ResolveContext {
  organizationId: string;
  orgSport?: string;         // organization.sports[0] — reliable sport source for the default duration
  divisions: any[];          // Division docs in this tournament
  teamDivisions: any[];      // DivisionTeam docs across this tournament's divisions
  sections: any[];           // League docs (contests) under this tournament
  locations: any[];          // Location docs
}

// Normalize for matching: case, whitespace, and TYPOGRAPHIC QUOTES. Team names
// registered on phones carry curly apostrophes (John’s, U+2019) while admins
// type straight ones (John's, 0x27) in spreadsheets — found the hard way when
// two of 424 otherwise-perfect rows in a real import failed only on that byte.
const lc = (v: any) =>
  typeof v === 'string'
    ? v.trim().toLowerCase().replace(/[‘’]/g, "'").replace(/[“”]/g, '"')
    : '';

// ── Placement-game placeholders ─────────────────────────────────────────────
//
// Placement/consolation games are scheduled before their participants are
// known: a team cell reads "1st Place - G01 Silver" instead of a team name.
// Those rows import as PLACEHOLDER games — no team ref, the label stored on
// homeTeamPlaceholder/awayTeamPlaceholder (Game schema fields the public
// PlayoffResults page renders directly).
//
// Detection is deliberately pattern-gated to "Nth Place …" so a misspelled
// real team name still errors instead of silently importing as a placeholder.
// ("Winner of …"/"Loser of …" labels are deliberately NOT accepted here: their
// playoffRound can't be derived from the label, and a playoff game without
// `playoffRound` set would be counted into the regular-season standings —
// StandingPage splits regular vs playoff on that field's truthiness.)
const PLACEMENT_RE = /^(\d+)(?:st|nd|rd|th)\s+place\b/i;

export const parsePlacementLabel = (value: string | undefined): { rank: number } | null => {
  const m = PLACEMENT_RE.exec((value || '').trim());
  if (!m) return null;
  const rank = parseInt(m[1], 10);
  return Number.isFinite(rank) && rank >= 1 ? { rank } : null;
};

// A placement game between ranks (1,2) is the championship, (3,4) the
// third-place game, and so on. Keyed by the better (lower) rank of the pair;
// values must stay within the Game schema's playoffRound enum.
export const placementRound = (
  bestRank: number
): { playoffRound: string; gameLabel: string } | null => {
  if (bestRank <= 2) return { playoffRound: 'championship', gameLabel: '1st Place' };
  if (bestRank <= 4) return { playoffRound: 'third_place', gameLabel: '3rd Place' };
  if (bestRank <= 6) return { playoffRound: 'fifth_place', gameLabel: '5th Place' };
  if (bestRank <= 8) return { playoffRound: 'seventh_place', gameLabel: '7th Place' };
  return null; // rank 9+: no matching enum value — treat as a normal (unknown) team name
};

const findByName = (collection: any[], name: string, nameField: string | string[] = 'name') => {
  const target = lc(name);
  if (!target) return { matches: [] };
  const fields = Array.isArray(nameField) ? nameField : [nameField];
  const matches = collection.filter((doc) =>
    fields.some((f) => lc(doc?.[f]) === target)
  );
  return { matches };
};

export function resolveGameRow(raw: RawGameRow, ctx: ResolveContext): ResolvedGameRow {
  const issues: Issue[] = [];

  // -- Division --
  let division: any = null;
  if (!raw.Division) {
    issues.push({ severity: 'error', field: 'Division', message: 'Division is required' });
  } else {
    const { matches } = findByName(ctx.divisions, raw.Division);
    if (matches.length === 0) {
      issues.push({
        severity: 'error',
        field: 'Division',
        message: `Division "${raw.Division}" not found in this tournament`,
      });
    } else {
      if (matches.length > 1) {
        issues.push({
          severity: 'warning',
          field: 'Division',
          message: `Multiple divisions match "${raw.Division}"; using first`,
        });
      }
      division = matches[0];
    }
  }

  // -- Section (derived from division.league) --
  let section: any = null;
  if (division) {
    const leagueId = helpers.getObjId(division.league);
    section = _.find(ctx.sections, (s) => s._id === leagueId) || null;
  }

  // -- Teams (must be in resolved division) --
  const teamsInDivision = division
    ? ctx.teamDivisions.filter((td) => helpers.getObjId(td.division) === division._id)
    : [];

  // Each side resolves to EITHER a team doc OR a placement-label placeholder.
  const resolveTeam = (
    label: 'Home Team' | 'Away Team',
    value: string | undefined
  ): { doc: any; placeholder: string | null; rank: number | null } => {
    const none = { doc: null, placeholder: null, rank: null };
    if (!value) {
      issues.push({ severity: 'error', field: label, message: `${label} is required` });
      return none;
    }
    if (!division) return none; // can't lookup teams without a division

    const target = lc(value);
    const matches = teamsInDivision.filter((td) => {
      const name = getTeamDisplayName(td) || td.name || td.teamName || '';
      return lc(name) === target;
    });
    if (matches.length === 0) {
      // No such team — is it a placement label ("3rd Place - B02 Silver")?
      // A real team match always wins over the pattern, so an actual team
      // named like this (unlikely) would still link normally.
      const placement = parsePlacementLabel(value);
      if (placement && placementRound(placement.rank)) {
        return { doc: null, placeholder: value.trim(), rank: placement.rank };
      }
      issues.push({
        severity: 'error',
        field: label,
        message: `${label} "${value}" not found in division`,
      });
      return none;
    }
    if (matches.length > 1) {
      issues.push({
        severity: 'warning',
        field: label,
        message: `Multiple teams match "${value}"; using first`,
      });
    }
    return { doc: matches[0], placeholder: null, rank: null };
  };

  const home = resolveTeam('Home Team', raw['Home Team']);
  const away = resolveTeam('Away Team', raw['Away Team']);
  const homeTeamDoc = home.doc;
  const awayTeamDoc = away.doc;

  // Placement metadata for the row: derived from the better (lower) rank on
  // either side — "1st Place vs 2nd Place" → championship, "3rd vs 4th" →
  // third_place. See the comment on PLACEMENT_RE for why this is mandatory.
  const isPlaceholder = !!(home.placeholder || away.placeholder);
  const bestRank = Math.min(home.rank ?? Infinity, away.rank ?? Infinity);
  const placement = isPlaceholder ? placementRound(bestRank) : null;

  // -- Date --
  let dateKey = '';
  if (!raw.Date) {
    issues.push({ severity: 'error', field: 'Date', message: 'Date is required' });
  } else {
    dateKey = toDateKey(raw.Date);
    if (!dateKey) {
      issues.push({
        severity: 'error',
        field: 'Date',
        message: `Date "${raw.Date}" is not a recognized format (use YYYY-MM-DD or M-DD-YYYY)`,
      });
    } else {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const parsed = new Date(`${dateKey}T00:00:00`);
      if (!Number.isNaN(parsed.getTime()) && parsed < today) {
        issues.push({
          severity: 'warning',
          field: 'Date',
          message: `Date is in the past`,
        });
      }
    }
  }

  // -- Time --
  let startMinutes = 0;
  let canonicalTime = '';
  if (!raw.Time) {
    issues.push({ severity: 'error', field: 'Time', message: 'Time is required' });
  } else if (!isParseableTime(raw.Time)) {
    issues.push({
      severity: 'error',
      field: 'Time',
      message: `Time "${raw.Time}" is not a recognized format (use "h:mm AM/PM" or "HH:mm")`,
    });
  } else {
    startMinutes = parseTimeToMinutes(raw.Time);
    canonicalTime = formatMinutesForGameTime(startMinutes);
  }

  // -- Duration -- (sport-aware default; the CSV Duration column overrides when present)
  let duration = getDefaultGameDuration(ctx.orgSport ?? division?.sports?.[0]);
  if (raw.Duration) {
    const parsed = parseInt(raw.Duration, 10);
    if (Number.isFinite(parsed) && parsed > 0) {
      duration = parsed;
      if (parsed > 480) {
        issues.push({
          severity: 'warning',
          field: 'Duration',
          message: `Duration ${parsed}m is unusually long`,
        });
      }
    } else {
      issues.push({
        severity: 'warning',
        field: 'Duration',
        message: `Duration "${raw.Duration}" is not a positive number; using default 60`,
      });
    }
  }

  // -- Location (optional) --
  let locationId: string | null = null;
  let locationName = '';
  if (raw.Location) {
    locationName = raw.Location;
    const { matches } = findByName(ctx.locations, raw.Location, ['name', 'displayName']);
    if (matches.length === 0) {
      issues.push({
        severity: 'warning',
        field: 'Location',
        message: `Location "${raw.Location}" not found; saving as free-text label`,
      });
    } else {
      if (matches.length > 1) {
        issues.push({
          severity: 'warning',
          field: 'Location',
          message: `Multiple locations match "${raw.Location}"; using first`,
        });
      }
      locationId = matches[0]._id;
      locationName = matches[0].displayName || matches[0].name || raw.Location;
    }
  }

  const hasError = issues.some((i) => i.severity === 'error');
  const hasWarning = issues.some((i) => i.severity === 'warning');
  const status = hasError ? 'error' : hasWarning ? 'warning' : 'ready';

  return {
    raw,
    payload: {
      division: division?._id ?? null,
      league: section?._id ?? null,
      organization: ctx.organizationId,
      homeTeam: homeTeamDoc?._id ?? null,
      awayTeam: awayTeamDoc?._id ?? null,
      globalDate: dateKey ? toGlobalDate(dateKey) : '',
      time: canonicalTime,
      duration,
      location: locationId,
      ...(locationId ? {} : { locationName: raw.Location || '' }),
      ...(isPlaceholder
        ? {
            homeTeamPlaceholder: home.placeholder || undefined,
            awayTeamPlaceholder: away.placeholder || undefined,
            playoffRound: placement?.playoffRound ?? null,
            gameLabel: placement?.gameLabel ?? '',
          }
        : {}),
    },
    status,
    issues,
    display: {
      divisionName: division?.name || raw.Division || '',
      homeTeamName: homeTeamDoc ? (getTeamDisplayName(homeTeamDoc) || raw['Home Team'] || '') : raw['Home Team'] || '',
      awayTeamName: awayTeamDoc ? (getTeamDisplayName(awayTeamDoc) || raw['Away Team'] || '') : raw['Away Team'] || '',
      sectionName: section?.name || '',
      locationName: locationName || raw.Location || '',
      dateKey,
      startMinutes,
      ...(isPlaceholder ? { isPlaceholder: true } : {}),
    },
  };
}

// Adds a "duplicate inside this CSV" warning to rows with the same
// division/homeTeam/awayTeam/dateKey/startMinutes.
export function flagDuplicates(rows: ResolvedGameRow[]): ResolvedGameRow[] {
  const seen = new Map<string, number>();
  return rows.map((row) => {
    if (row.status === 'error') return row;
    const key = [
      row.payload.division,
      // Placeholder rows have null team refs, so two DIFFERENT placement games
      // in the same division at the same time (championship + 3rd-place on
      // adjacent courts) would collide on team ids alone — key on the labels
      // for those rows instead.
      row.payload.homeTeam ?? row.payload.homeTeamPlaceholder ?? '',
      row.payload.awayTeam ?? row.payload.awayTeamPlaceholder ?? '',
      row.display.dateKey,
      row.display.startMinutes,
    ].join('|');
    const firstAt = seen.get(key);
    if (firstAt === undefined) {
      seen.set(key, row.raw.rowIndex);
      return row;
    }
    const newIssues: Issue[] = [
      ...row.issues,
      {
        severity: 'warning',
        field: 'general',
        message: `Duplicates row ${firstAt} (same division/teams/date/time)`,
      },
    ];
    return {
      ...row,
      issues: newIssues,
      status: row.status === 'ready' ? 'warning' : row.status,
    };
  });
}
